/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * i18n Configuration — Namespace-based Lazy Loading
 * ============================================================================
 *
 * Namespaces:
 *   common   — statically bundled (always available, no network fetch)
 *   auth     — lazy loaded on auth routes
 *   core     — lazy loaded after login (ProtectedRoute)
 *   platform — lazy loaded on SuperAdmin routes (EN only)
 *   venta    — lazy loaded on /venta/* routes
 *
 * Key paths are unchanged — each namespace file preserves its top-level
 * wrapper (e.g. venta.json contains { "venta": { ... } }), so all existing
 * t('venta.products.title') calls resolve identically.
 *
 * ============================================================================
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import resourcesToBackend from 'i18next-resources-to-backend';

// Static bundle: common translations (always available, no load delay)
import commonEn from '@/locales/en/common.json';
import commonFr from '@/locales/fr/common.json';

i18n
  .use(resourcesToBackend((language: string, namespace: string) =>
    import(`../locales/${language}/${namespace}.json`)
  ))
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    partialBundledLanguages: true,
    resources: {
      en: { common: commonEn },
      fr: { common: commonFr },
    },
    ns: ['common'],
    defaultNS: 'common',
    fallbackNS: 'common',
    fallbackLng: 'en',
    supportedLngs: ['en', 'fr'],
    debug: false,
    interpolation: {
      escapeValue: false, // React already escapes values
    },
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'corex_language',
    },
    react: {
      useSuspense: true,
    },
  });

export default i18n;

/**
 * Available languages
 */
export const languages = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
] as const;

export type LanguageCode = (typeof languages)[number]['code'];
